import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { RestaurantContext } from 'Contexts/RestaurantContext';

/**
 * A request to open Support on one topic, e.g. "Abrechnungssupport" from
 * Buchhaltung. The Support panel takes it once its templates are loaded and
 * only prepares that topic: nothing is sent until the user writes.
 */
export type SupportTopicRequest = {
  /** Increases per request, so asking again for the same topic is new. */
  id: number;
  templateId: string;
  source?: string;
  /** A request for another restaurant than the panel's is dropped. */
  restaurantId: string;
};

type SupportRequestValue = {
  request: SupportTopicRequest | null;
  openSupportTopic: (templateId: string, source?: string) => void;
  /** Marks the request as handled; a newer request is kept. */
  consumeSupportTopic: (id: number) => void;
  /** Set until the Support panel has opened its "Neuigkeiten" view. */
  changelogRequested: boolean;
  requestSupportChangelog: () => void;
  consumeSupportChangelog: () => void;
};

export const SupportRequestContext = createContext<SupportRequestValue>({
  request: null,
  openSupportTopic: () => {},
  consumeSupportTopic: () => {},
  changelogRequested: false,
  requestSupportChangelog: () => {},
  consumeSupportChangelog: () => {},
});

export const BILLING_SUPPORT_TEMPLATE_ID = 'billing';

type Props = {
  /** Mobile shows Support as its own route instead of the inset panel. */
  isMobile: boolean;
  children: React.ReactNode;
};

export const SupportRequestProvider = ({ isMobile, children }: Props) => {
  const { restaurantId, setSupportOpen } = useContext(RestaurantContext);
  const history = useHistory();
  const [request, setRequest] = useState<SupportTopicRequest | null>(null);
  const [changelogRequested, setChangelogRequested] = useState(false);
  const lastId = useRef(0);

  const openSupportTopic = useCallback(
    (templateId: string, source?: string) => {
      if (!restaurantId) return;
      lastId.current += 1;
      setRequest({ id: lastId.current, templateId, restaurantId, source: source ?? history.location.pathname });
      if (isMobile) history.push(`/${restaurantId}/support`);
      else setSupportOpen(true);
    },
    [restaurantId, isMobile, history, setSupportOpen],
  );

  const consumeSupportTopic = useCallback(
    (id: number) =>
      setRequest((current) => (current?.id === id ? null : current)),
    [],
  );

  const requestSupportChangelog = useCallback(
    () => setChangelogRequested(true),
    [],
  );
  const consumeSupportChangelog = useCallback(
    () => setChangelogRequested(false),
    [],
  );

  const value = useMemo(
    () => ({
      request,
      openSupportTopic,
      consumeSupportTopic,
      changelogRequested,
      requestSupportChangelog,
      consumeSupportChangelog,
    }),
    [
      request,
      openSupportTopic,
      consumeSupportTopic,
      changelogRequested,
      requestSupportChangelog,
      consumeSupportChangelog,
    ],
  );

  return (
    <SupportRequestContext.Provider value={value}>
      {children}
    </SupportRequestContext.Provider>
  );
};

export const useSupportTopic = () => useContext(SupportRequestContext);
